import { useDispatch, useSelector } from "react-redux";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { addWeeks, getWeek, isBefore, startOfWeek } from "date-fns";

import { getEventFullAddress, getFormattedTime } from "../../utils";
import { AppDispatch, RootState } from "../../store";
import { BASE_URL, POD_URL, PRODUCT_SELECTION_URL } from "../../Routes/urls";
import { orderActions } from "../../store/slices/OrderSlice";
import { setCurrEvent } from "../../store/actions/EventActions";
import {
  fetchEvents,
  fetchEventRegistrationCount,
} from "../../services/events";
import { eventTypes } from "../../ts/consts";
import { RegistrationCount } from "../../ts/types";
import { warningActions } from "../../store/slices/WarningSlice";
import WeekCalendar from "../../components/WeekCalendar/WeekCalendar";
import Card from "../../components/Card/Card";
import Event, { SelectedService } from "../../ts/models/Event";
import classes from "./EventCalendar.module.css";
import timeIcon from "../../assets/icons/time.svg";
import locationIcon from "../../assets/icons/location.svg";
import TabBar from "../../components/TabBar/TabBar";
import ServiceStatus from "../../components/Status/ServiceStatus";

export const EventCalendar = () => {
  const navigate = useNavigate();
  const dispatch: AppDispatch = useDispatch();
  const { pod, resourceName } = useSelector((state: RootState) => state.pod);

  const [isHome, setIsHome] = useState(false);
  const [nextPrevCount, setNextPrevCount] = useState(0);
  const [events, setEvents] = useState<Event[]>([]);
  const [registrationCountData, setRegistrationCountData] = useState<
    RegistrationCount[]
  >([]);
  const [upcomingEventStartDate, setUpcomingEventStartDate] = useState<any>();

  const searchParams = new URLSearchParams(window.location.search);
  const patientId = searchParams.get("p_id");
  const token = searchParams.get("t_id");

  const filteredEvents = events.filter((event) =>
    isHome ? event.isHome === isHome : true,
  );

  const podId = pod.id;

  const renderEventItem = (eventItem: Event, date: Date) => {
    return (
      <>
        {eventItem.endTime >= Date.now() && (
          <EventItem
            item={eventItem}
            date={date}
            key={eventItem.id}
            registrationCountData={registrationCountData}
          />
        )}
      </>
    );
  };

  const canSelectEvent = (event: Event, date: Date): boolean => {
    const data = registrationCountData.find(
      (data) => data.eventId === event.id,
    );

    if (data) {
      const eventDate =
        date.getTime() - new Date(date).getTimezoneOffset() * 60000;
      const registration = data.registrations.filter(
        (registration) => registration.eventTime === eventDate,
      );

      // Check if at least one service is available.
      // If no one signed up yet for a service, or
      // if the signup count is below the available capacity.
      if (registration.length < event.services.length) {
        return true;
      }
      const isSlotAvailable = registration.some(
        (registration) => registration.signupCount < event.capacity,
      );

      return isSlotAvailable;
    }
    return true;
  };

  const getServicesAvailable = (event: Event, date: Date) => {
    const data = registrationCountData.find(
      (data) => data.eventId === event.id,
    );

    if (data) {
      const eventDate =
        date.getTime() - new Date(date).getTimezoneOffset() * 60000;
      const servicesAvailable: SelectedService[] = [];

      event.services.forEach((service) => {
        const registration = data.registrations.find(
          (registration) =>
            registration.serviceId === service.serviceId &&
            registration.eventTime === eventDate,
        );

        // Check if the service is available.
        // If no one signed up yet for the service, or
        // if the signup count is below the available capacity.
        if (!registration || registration.signupCount < event.capacity) {
          servicesAvailable.push(service);
        }
      });

      return servicesAvailable;
    }
    return event.services;
  };

  const eventSelectHandler = (event: Event, date: Date) => {
    if (canSelectEvent(event, date)) {
      const availableServices = getServicesAvailable(event, date);
      navigate(
        `${BASE_URL}/${resourceName}/${POD_URL}/${podId}/event/${event.id}/${PRODUCT_SELECTION_URL}${window.location.search}`,
      );
      // Removing previously selected service and products.
      dispatch(orderActions.removeServicesWithProducts());
      dispatch(setCurrEvent(event, date.getTime(), availableServices));
    } else {
      dispatch(
        warningActions.showWarning({
          message: "No slots are available for this date.",
        }),
      );
    }
  };

  const tabChangeHandler = (isHome: string) => {
    setIsHome(!!isHome);
  };

  const fetchEventData = async (startDate: Date) => {
    if (podId) {
      const filter = {
        offset: 0,
        where: {
          podId,
          startDate: startDate.valueOf(),
          endDate: addWeeks(startDate, 1).valueOf(),
          isActive: true,
        },
      };
      try {
        const { status, data } = await fetchEvents(filter);
        if (status === 200) {
          setEvents(data);
          setUpcomingEventStartDate(null);
        }
      } catch (error) {
        console.log(error);
      }
    }
  };

  const fetchUpcomingEventData = async () => {
    if (podId) {
      const filter = {
        offset: 0,
        where: {
          podId,
          startDate: new Date().valueOf(),
          isActive: true,
        },
        sortBy: "startTime ASC", // sort to get earliest upcoming event
        limit: 1, // only need the next upcoming event
      };

      try {
        const { status, data } = await fetchEvents(filter);

        if (status === 200 && data.length > 0) {
          const currentWeekStart = startOfWeek(new Date(), { weekStartsOn: 1 });

          let upcomingEventDate = new Date(data[0].startTime);

          // If the event start date is before the current week start, treat it as current week start
          if (isBefore(upcomingEventDate, currentWeekStart)) {
            upcomingEventDate = currentWeekStart;
          }

          // Calculate the number of weeks between the current week and the event's week
          const currentWeek = getWeek(new Date());
          const upcomingEventWeek = getWeek(upcomingEventDate);
          const eventWeekCount = upcomingEventWeek - currentWeek;
          setNextPrevCount(eventWeekCount);

          // Start of the week
          const weekStartDate = startOfWeek(upcomingEventDate, {
            weekStartsOn: 1,
          }); // 1 = Monday

          // Now fetch all events in that week
          const weekFilter = {
            offset: 0,
            where: {
              podId,
              startDate: weekStartDate.valueOf(),
              endDate: addWeeks(weekStartDate, 1).valueOf(),
              isActive: true,
            },
          };

          const weekResponse = await fetchEvents(weekFilter);
          if (weekResponse.status === 200) {
            setEvents(weekResponse.data);
            setUpcomingEventStartDate(upcomingEventDate.getTime());
          }
        } else {
          setEvents([]);
        }
      } catch (error) {
        console.log(error);
      }
    }
  };

  useEffect(() => {
    fetchUpcomingEventData();
  }, [pod]);

  const fetchEventRegistrationCountData = async (eventIds: string[]) => {
    try {
      const registrationCountRes = await fetchEventRegistrationCount(eventIds);
      if (registrationCountRes.status) {
        setRegistrationCountData(registrationCountRes.data);
      }
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    if (events.length) {
      const eventIds = events.map((event) => event.id);
      fetchEventRegistrationCountData(eventIds);
    }
  }, [events]);

  useEffect(() => {
    if (token && patientId && localStorage.getItem("patientData")) {
      localStorage.removeItem("patientData");
      localStorage.removeItem("isPatientPortalData");
    }
  }, [token, patientId]);

  const nextClickHandler = (weekStart: Date) => {
    fetchEventData(weekStart);
    setNextPrevCount((prevState) => prevState + 1);
  };

  const prevClickHandler = (weekStart: Date) => {
    fetchEventData(weekStart);
    setNextPrevCount((prevState) => prevState - 1);
  };

  return (
    <div className={classes.main}>
      <div className={classes.container}>
        <div className={classes.heading}>
          To Get Started, Choose a Location:
        </div>
        <Card className={classes.card}>
          <TabBar
            tabs={eventTypes}
            idExtractor='id'
            onTabChange={tabChangeHandler}
          />
          <div className={classes.calendarContainer}>
            <WeekCalendar
              calendarItems={filteredEvents}
              renderCalendarItem={renderEventItem}
              startDate={upcomingEventStartDate}
              onNextWeek={nextClickHandler}
              onPrevWeek={prevClickHandler}
              onItemSelect={eventSelectHandler}
              disablePrev={nextPrevCount <= 0}
            />
          </div>
        </Card>
      </div>
    </div>
  );
};

type Props = {
  item: Event;
  date: Date;
  registrationCountData: RegistrationCount[];
};

const EventItem: React.FC<Props> = ({ item, date, registrationCountData }) => {
  const servicesMaster = useSelector(
    (state: RootState) => state.masters.services,
  );

  // Find the registration counts data for this event.
  const data = registrationCountData.find((data) => data.eventId === item.id);

  const serviceData = item.services.map((eventService) => {
    const masterService = servicesMaster.find(
      (service) => service.id === eventService.serviceId,
    );

    var leftCapacity = item.capacity;
    if (data) {
      // Find the registration count for the service on specified date.
      const registration = data.registrations.find(
        (registration) =>
          registration.serviceId === eventService.serviceId &&
          registration.eventTime ===
            date.getTime() - new Date(date).getTimezoneOffset() * 60000,
      );

      if (registration?.signupCount) {
        leftCapacity -= registration.signupCount;
      }
    }

    if (masterService) {
      return {
        serviceName: masterService.name,
        leftCapacity,
      };
    } else {
      return {
        serviceName: "",
        leftCapacity,
      };
    }
  });

  return (
    <div className={classes.eventItem}>
      <div className={classes.basicInfo}>
        <p className={classes.eventName}>{item.name}</p>
      </div>
      <div className={classes.timeAndLocationInfo}>
        <div className={classes.time}>
          <img
            src={timeIcon}
            alt='Time Icon'
            style={{ marginRight: "0.5rem" }}
          />
          <div className={classes.startEndTime}>
            <span style={{ display: "block" }}>{`${getFormattedTime(
              item.startTime,
            )} -`}</span>
            <span>{`${getFormattedTime(item.endTime)}`}</span>
          </div>
        </div>
        <div className={classes.location}>
          <img
            src={locationIcon}
            alt='Location Icon'
            style={{ marginRight: "0.5rem" }}
          />
          <div>{getEventFullAddress(item)}</div>
        </div>
      </div>
      <div className={classes.medServices}>
        {serviceData.map((service, idx) => (
          <ServiceStatus
            key={idx}
            serviceName={service.serviceName}
            count={service.leftCapacity}
          />
        ))}
      </div>
    </div>
  );
};

export default EventCalendar;
